import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Search, 
  BookOpen, 
  FolderOpen, 
  Plus, 
  Eye, 
  Clock, 
  User, 
  Tag, 
  Play, 
  Image as ImageIcon, 
  Edit, 
  Trash2, 
  Loader2,
  Youtube,
  Users,
  Building2,
  ListOrdered
} from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const KnowledgeBasePage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Article detail
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  
  // Create/Edit modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category_id: '',
    target_audience: 'all',
    tags: '',
    youtube_url: '',
    cover_image_url: '',
    steps: []
  });

  const isAgency = user?.role === 'agency' || user?.role === 'project_manager';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory !== 'all') params.category_id = selectedCategory;
      
      const [articlesRes, categoriesRes] = await Promise.all([
        api.get('/knowledge/articles', { params }),
        api.get('/knowledge/categories')
      ]);
      
      setArticles(articlesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openArticle = async (article) => {
    try {
      const res = await api.get(`/knowledge/articles/${article.id}`);
      setSelectedArticle(res.data);
      setShowArticleModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const openCreateModal = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title || '',
        content: article.content || '',
        category_id: article.category_id || '',
        target_audience: article.target_audience || 'all',
        tags: (article.tags || []).join(', '),
        youtube_url: article.youtube_url || '',
        cover_image_url: article.cover_image_url || '',
        steps: article.steps || []
      });
    } else {
      setEditingArticle(null);
      setFormData({
        title: '',
        content: '',
        category_id: '',
        target_audience: 'all',
        tags: '',
        youtube_url: '',
        cover_image_url: '',
        steps: []
      });
    }
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        category_id: formData.category_id || null,
        target_audience: formData.target_audience,
        tags: formData.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean),
        youtube_url: formData.youtube_url || null,
        cover_image_url: formData.cover_image_url || null,
        steps: formData.steps
      };
      
      if (editingArticle) {
        await api.put(`/knowledge/articles/${editingArticle.id}`, payload);
        toast.success('Article mis à jour');
      } else {
        await api.post('/knowledge/articles', payload);
        toast.success('Article créé');
      }
      
      setShowCreateModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    
    try {
      await api.delete(`/knowledge/articles/${articleId}`);
      toast.success('Article supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, { order: prev.steps.length + 1, title: '', content: '', image_url: '' }]
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 }))
    }));
  };

  const getAudienceIcon = (audience) => {
    switch (audience) {
      case 'clients': return <Building2 className="w-3 h-3" />;
      case 'creatives': return <Users className="w-3 h-3" />;
      default: return <Eye className="w-3 h-3" />;
    }
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case 'clients': return 'Clients';
      case 'creatives': return 'Membres';
      case 'internal': return 'Interne';
      default: return 'Tous';
    }
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Non catégorisé';
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Base de connaissances
            </h1>
            <p className="text-slate-500">Tutoriels, guides et ressources</p>
          </div>
          
          {isAgency && (
            <Button onClick={() => openCreateModal()} style={{ backgroundColor: theme.colors?.accent }}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvel article
            </Button>
          )}
        </div>

        {/* Search and filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un tutoriel..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Articles grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Aucun article trouvé</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map(article => (
              <Card 
                key={article.id} 
                className="cursor-pointer hover:shadow-lg transition-shadow group"
                onClick={() => openArticle(article)}
              >
                {article.cover_image_url && (
                  <div className="h-40 bg-slate-100 rounded-t-lg overflow-hidden">
                    <img 
                      src={article.cover_image_url} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start gap-2 mb-2">
                    {article.youtube_url && (
                      <Badge className="bg-red-100 text-red-700 text-xs">
                        <Play className="w-3 h-3 mr-1" />
                        Vidéo
                      </Badge>
                    )}
                    {article.steps?.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                        <ListOrdered className="w-3 h-3 mr-1" />
                        {article.steps.length} étapes
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs ml-auto">
                      {getAudienceIcon(article.target_audience)}
                      <span className="ml-1">{getAudienceLabel(article.target_audience)}</span>
                    </Badge>
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600 transition-colors">
                    {article.title}
                  </h3>
                  
                  <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                    {article.content?.substring(0, 120)}...
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <FolderOpen className="w-3 h-3" />
                      {getCategoryName(article.category_id)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {article.views || 0} vues
                    </span>
                  </div>
                  
                  {article.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {article.tags.slice(0, 3).map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Article Detail Modal */}
        <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedArticle && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="text-xl">{selectedArticle.title}</DialogTitle>
                    {isAgency && (
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => {
                          setShowArticleModal(false);
                          openCreateModal(selectedArticle);
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => {
                          handleDelete(selectedArticle.id);
                          setShowArticleModal(false);
                        }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Meta info */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {selectedArticle.author_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(selectedArticle.created_at).toLocaleDateString('fr-FR')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {selectedArticle.views} vues
                    </span>
                  </div>
                  
                  {/* Cover image */}
                  {selectedArticle.cover_image_url && (
                    <img 
                      src={selectedArticle.cover_image_url} 
                      alt={selectedArticle.title}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  )}
                  
                  {/* YouTube video */}
                  {selectedArticle.youtube_url && getYoutubeEmbedUrl(selectedArticle.youtube_url) && (
                    <div className="relative pt-[56.25%] rounded-lg overflow-hidden bg-slate-900">
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={getYoutubeEmbedUrl(selectedArticle.youtube_url)}
                        title={selectedArticle.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="prose prose-slate max-w-none">
                    <p className="whitespace-pre-wrap">{selectedArticle.content}</p>
                  </div>
                  
                  {/* Steps */}
                  {selectedArticle.steps?.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <ListOrdered className="w-5 h-5" />
                        Étapes du tutoriel
                      </h3>
                      {selectedArticle.steps.map((step, index) => (
                        <div key={index} className="p-4 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold shrink-0">
                              {step.order || index + 1}
                            </div>
                            <div className="flex-1">
                              {step.title && (
                                <h4 className="font-semibold mb-2">{step.title}</h4>
                              )}
                              <p className="text-slate-600 whitespace-pre-wrap">{step.content}</p>
                              {step.image_url && (
                                <img 
                                  src={step.image_url} 
                                  alt={step.title || `Étape ${step.order}`}
                                  className="mt-3 rounded-lg max-h-64 object-contain"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Tags */}
                  {selectedArticle.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.tags.map(tag => (
                        <Badge key={tag} variant="secondary">
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
              </DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="content">
              <TabsList className="mb-4">
                <TabsTrigger value="content">Contenu</TabsTrigger>
                <TabsTrigger value="steps">Étapes</TabsTrigger>
                <TabsTrigger value="media">Médias</TabsTrigger>
                <TabsTrigger value="settings">Paramètres</TabsTrigger>
              </TabsList>
              
              <TabsContent value="content" className="space-y-4">
                <div>
                  <Label>Titre *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Titre de l'article"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contenu</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Contenu de l'article..."
                    className="mt-1"
                    rows={8}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="steps" className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <Label>Étapes du tutoriel</Label>
                  <Button variant="outline" size="sm" onClick={addStep}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter une étape
                  </Button>
                </div>
                
                {formData.steps.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">
                    Aucune étape. Cliquez sur &quot;Ajouter une étape&quot; pour créer un tutoriel pas à pas.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {formData.steps.map((step, index) => (
                      <div key={index} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium">Étape {index + 1}</span>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeStep(index)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-3">
                          <Input
                            placeholder="Titre de l'étape (optionnel)"
                            value={step.title}
                            onChange={(e) => updateStep(index, 'title', e.target.value)}
                          />
                          <Textarea
                            placeholder="Description de l'étape..."
                            value={step.content}
                            onChange={(e) => updateStep(index, 'content', e.target.value)}
                            rows={3}
                          />
                          <Input
                            placeholder="URL de l'image (optionnel)"
                            value={step.image_url}
                            onChange={(e) => updateStep(index, 'image_url', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="media" className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Image de couverture
                  </Label>
                  <Input
                    value={formData.cover_image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, cover_image_url: e.target.value }))}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-red-500" />
                    Vidéo YouTube
                  </Label>
                  <Input
                    value={formData.youtube_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Collez l&apos;URL d&apos;une vidéo YouTube pour l&apos;intégrer à l&apos;article
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="settings" className="space-y-4">
                <div>
                  <Label>Catégorie</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, category_id: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Aucune</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Audience cible</Label>
                  <Select 
                    value={formData.target_audience} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, target_audience: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous (membres et clients)</SelectItem>
                      <SelectItem value="creatives">Membres uniquement</SelectItem>
                      <SelectItem value="clients">Clients uniquement</SelectItem>
                      <SelectItem value="internal">Interne (agence)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tags (séparés par des virgules)</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="tutoriel, guide, débutant"
                    className="mt-1"
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingArticle ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default KnowledgeBasePage;
